import * as React from "react";
import { getLabelForHighDefinitionDigitalStatus } from "../../../../../helpers";
import { makeStyles } from "@mui/styles";
import {
  markOrderAsHighDefinitionDigitalCompletedMutation,
  markOrderAsHighDefinitionDigitalPausedMutation,
  markOrderAsHighDefinitionDigitalStartedMutation
} from "../../../../../graph";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import JobStatusIcon from "../../../../../components/JobStatusIcon";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../../../components/withSnackbar";

const useStyles = makeStyles(theme => ({
  buttonContainer: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: theme.spacing(1)
  },

  jobStatus: {
    marginLeft: theme.spacing(1)
  },

  spaceBetweenContainer: {
    display: "flex"
  },

  spacerRight: {
    marginRight: theme.spacing(1)
  },

  spinnerWrapper: {
    padding: theme.spacing(1),
    position: "relative",
    textAlign: "center"
  }
}));

const HDDOrderStatus = ({
  order: {
    id: orderId,
    canViewerCompleteHighDefinitionDigital,
    canViewerPauseHighDefinitionDigital,
    canViewerStartHighDefinitionDigital,
    highDefinitionDigitalStatus
  },
  showErrorSnackbar,
  showSuccessSnackbar
}) => {
  const classes = useStyles();

  // States:
  const [isMutatingOrder, setIsMutatingOrder] = useState(false);

  // Mutations:
  const [markOrderAsHighDefinitionDigitalCompleted] = useMutation(
    markOrderAsHighDefinitionDigitalCompletedMutation
  );
  const [markOrderAsHighDefinitionDigitalPaused] = useMutation(
    markOrderAsHighDefinitionDigitalPausedMutation
  );
  const [markOrderAsHighDefinitionDigitalStarted] = useMutation(
    markOrderAsHighDefinitionDigitalStartedMutation
  );

  // Handlers:
  const handleCompleteButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsHighDefinitionDigitalCompleted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsHighDefinitionDigitalCompleted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handlePauseButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsHighDefinitionDigitalPaused({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsHighDefinitionDigitalPaused: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  const handleStartButtonClick = () => {
    setIsMutatingOrder(true);
    markOrderAsHighDefinitionDigitalStarted({ variables: { orderId } })
      .then(
        ({
          data: {
            markOrderAsHighDefinitionDigitalStarted: { succeeded, errors }
          }
        }) => {
          if (succeeded) {
            showSuccessSnackbar(`The order has been updated`);
          } else {
            showErrorSnackbar(
              `Something went wrong: ${errors.orderId.join(", ")}`
            );
          }
        }
      )
      .catch(e => {
        showErrorSnackbar(`Something went wrong: ${e.message}`);
      })
      .finally(() => setIsMutatingOrder(false));
  };

  return (
    <div>
      <div className={classes.spaceBetweenContainer}>
        <JobStatusIcon status={highDefinitionDigitalStatus} />
        <div className={classes.jobStatus}>
          <Typography variant="h6">HDD</Typography>
          <Typography variant="body2">
            {getLabelForHighDefinitionDigitalStatus(
              highDefinitionDigitalStatus
            )}
          </Typography>
        </div>
      </div>
      {isMutatingOrder ? (
        <div className={classes.spinnerWrapper}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.buttonContainer}>
          {canViewerStartHighDefinitionDigital && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleStartButtonClick}
            >
              Start
            </Button>
          )}
          {canViewerCompleteHighDefinitionDigital && (
            <Button
              className={classes.spacerRight}
              color="primary"
              variant="contained"
              onClick={handleCompleteButtonClick}
            >
              HDD Complete
            </Button>
          )}
          {canViewerPauseHighDefinitionDigital && (
            <Button
              color="secondary"
              onClick={handlePauseButtonClick}
              variant="contained"
            >
              Pause
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default withSnackbar(HDDOrderStatus);
